import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { prefetchWorkspaceCompanies } from '../../utils/workspaceCompanies';
import styles from './CheckoutReturnHandler.module.css';

/** Bounded retries. Total wait is ~16s plus request time; never infinite. */
export const teamsCheckoutReturnConfig = {
  pollDelaysMs: [0, 500, 1000, 1500, 2000, 3000, 4000, 5000],
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function lookupErrorCode(error) {
  return error?.response?.data?.error || error?.code || null;
}

/**
 * After Stripe Checkout the webhook grants access, not this redirect.
 * The handler asks the server which workspace this Checkout Session created.
 */
export default function CheckoutReturnHandler() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh, selectCompany } = useWorkspace();
  const { refresh: refreshEntitlements } = useEntitlements();
  const [phase, setPhase] = useState(null);
  const [message, setMessage] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const checkout = params.get('checkout');
  const product = params.get('product');
  const sessionId = params.get('session_id');
  const active = checkout === 'success' && product === 'teams';

  useEffect(() => {
    if (!active) {
      setPhase(null);
      setMessage(null);
      return undefined;
    }
    if (!localStorage.getItem('token')) return undefined;

    if (!sessionId) {
      setPhase('recovery');
      setMessage('Payment was received, but this page is missing the checkout session. Open Home to find your workspace.');
      return undefined;
    }

    let cancelled = false;
    setPhase('setup');
    setMessage('Setting up your workspace…');

    (async () => {
      try {
        if (typeof refreshEntitlements === 'function') await refreshEntitlements();
      } catch {
        // Entitlement refresh is best-effort; the webhook is the source of access.
      }

      for (const delay of teamsCheckoutReturnConfig.pollDelaysMs) {
        if (delay) await sleep(delay);
        if (cancelled) return;

        let result;
        try {
          const res = await api.get('/api/billing/checkout-return', {
            params: { session_id: sessionId },
          });
          result = res?.data || null;
        } catch (err) {
          const code = lookupErrorCode(err);
          if (code === 'session_forbidden' || code === 'invalid_session' || err?.response?.status === 403) {
            setPhase('recovery');
            setMessage('We could not match this payment to your account. Open Home if your workspace is already there.');
            return;
          }
          if (err?.response?.status === 404) {
            setPhase('recovery');
            setMessage('That checkout session was not found. Open Home if your workspace is already there.');
            return;
          }
          continue;
        }

        if (!result || result.status === 'pending') continue;
        if (result.status === 'ignored') {
          setPhase(null);
          setMessage(null);
          return;
        }
        if (result.status !== 'ready' || !result.enterpriseId) continue;

        const company = {
          id: result.enterpriseId,
          name: result.name || 'Company',
          isAdmin: !!result.isAdmin,
          isOwner: !!result.isOwner,
        };
        try {
          await prefetchWorkspaceCompanies();
        } catch {
          // Switcher can refresh on the next navigation.
        }
        if (typeof refresh === 'function') {
          try {
            await refresh();
          } catch {
            // Best-effort after the server has named the workspace.
          }
        }
        if (typeof selectCompany === 'function') selectCompany(company.id, company);
        if (cancelled) return;
        setPhase(null);
        setMessage(null);
        navigate(`/enterprise-app/${company.id}`, { replace: true });
        return;
      }

      if (!cancelled) {
        setPhase('recovery');
        setMessage(
          'Setting up your workspace is taking longer than expected. Payment was received — refresh this page, or open Home when Stripe has confirmed it.'
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, sessionId, attempt, refresh, refreshEntitlements, selectCompany, navigate]);

  if (!active || !phase) return null;

  return (
    <div className={styles.banner} role="status">
      <p className={styles.message}>{message}</p>
      {phase === 'recovery' ? (
        <div className={styles.actions}>
          {sessionId ? (
            <button type="button" className={styles.retry} onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
          ) : null}
          <Link className={styles.home} to="/home">
            Go to Home
          </Link>
        </div>
      ) : null}
    </div>
  );
}
